// PlanService.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';

// Utilities
import { generateKey } from 'utilities/Keys';

import { collections, db } from 'firebaseConfig';

export const PLAN_STATUS = Object.freeze({
  ACTIVE: 'active',
  ARCHIVED: 'archived',
});

export const PLAN_PRICING_MODEL = Object.freeze({
  FREE: 'free',
  PER_SEAT: 'per_seat',
});

export const PLAN_UPLOAD_OPTIONS = Object.freeze([
  { label: '10 MB', value: 10 * 1024 * 1024 },
  { label: '25 MB', value: 25 * 1024 * 1024 },
  { label: '50 MB', value: 50 * 1024 * 1024 },
  { label: '100 MB', value: 100 * 1024 * 1024 },
  { label: '250 MB', value: 250 * 1024 * 1024 },
  { label: '500 MB', value: 500 * 1024 * 1024 },
]);

export const PLAN_LIVE_DEFAULTS = Object.freeze({
  liveConcurrentRoomsCap: 1,
  liveEgressMinutesMonthlyCap: 0,
  liveEnabled: false,
  liveIdleAutoEndMinutes: 5,
  liveMaxParticipantsPerRoom: 8,
  liveMaxSessionMinutes: 90,
  liveParticipantMinutesMonthlyCap: 0,
  liveRecordingEnabled: false,
});

const DEFAULT_POOLED_STORAGE_BYTES = 2 * 1024 * 1024 * 1024;
const CURRENCY_USD = 'USD';

/**
 * normalizeString
 *
 * Normalizes an input value to a trimmed string.
 *
 * @param {unknown} value - Input value to normalize.
 *
 * @returns {string} Trimmed string value.
 */
function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * normalizeNonNegativeInteger
 *
 * Returns a non-negative integer with a fallback when parsing fails.
 *
 * @param {unknown} value - Value to parse.
 * @param {number} fallback - Fallback integer value.
 *
 * @returns {number} Parsed integer or fallback.
 */
function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;
  return whole;
}

/**
 * normalizeNullableInteger
 *
 * Returns a positive integer or null.
 *
 * @param {unknown} value - Value to parse.
 *
 * @returns {number|null} Positive integer or null.
 */
function normalizeNullableInteger(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const whole = Math.floor(parsed);
  if (whole < 1) return null;
  return whole;
}

/**
 * normalizeStatus
 *
 * Ensures status is one of the allowed values.
 *
 * @param {unknown} value - Raw status value.
 *
 * @returns {string} Normalized status value.
 */
function normalizeStatus(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === PLAN_STATUS.ARCHIVED) {
    return PLAN_STATUS.ARCHIVED;
  }
  return PLAN_STATUS.ACTIVE;
}

/**
 * normalizePricingModel
 *
 * Ensures pricingModel is one of the allowed values.
 *
 * @param {unknown} value - Raw pricing model value.
 *
 * @returns {string} Normalized pricing model value.
 */
function normalizePricingModel(value) {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === PLAN_PRICING_MODEL.PER_SEAT) {
    return PLAN_PRICING_MODEL.PER_SEAT;
  }
  return PLAN_PRICING_MODEL.FREE;
}

/**
 * normalizePubliclyVisible
 *
 * Defaults to true when the visibility flag is omitted.
 *
 * @param {unknown} value - Raw visibility value.
 *
 * @returns {boolean} Normalized visibility value.
 */
function normalizePubliclyVisible(value) {
  if (value == null) {
    return true;
  }
  return Boolean(value);
}

/**
 * normalizePositiveIntegerWithFallback
 *
 * Returns a positive integer or the fallback value.
 *
 * @param {unknown} value - Value to parse.
 * @param {number} fallback - Fallback integer value.
 *
 * @returns {number} Positive integer value.
 */
function normalizePositiveIntegerWithFallback(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 1) return fallback;
  return whole;
}

/**
 * normalizePlanKey
 *
 * Returns a generated immutable plan key when no valid key exists.
 *
 * @param {unknown} value - Existing key value.
 *
 * @returns {string} Plan key.
 */
function normalizePlanKey(value) {
  const normalized = normalizeString(value);
  if (normalized) {
    return normalized;
  }
  return `plan_${generateKey(20)}`;
}

/**
 * buildPlanPayload
 *
 * Normalizes a plan object into the persisted Firestore schema.
 *
 * @param {object} input - Raw plan payload.
 * @param {number} input.sort - Sort index.
 *
 * @returns {object} Normalized plan payload.
 */
function buildPlanPayload(input = {}) {
  const key = normalizePlanKey(input.key);
  const pricingModel = normalizePricingModel(input.pricingModel);
  const seatPriceRaw = normalizeNonNegativeInteger(input.seatPrice, 0);
  const seatPrice = pricingModel === PLAN_PRICING_MODEL.FREE ? 0 : seatPriceRaw;
  const stripePriceId = pricingModel === PLAN_PRICING_MODEL.FREE
    ? null
    : normalizeString(input.stripePriceId) || null;
  const apiEnabled = Boolean(input.apiEnabled);
  const apiMonthlyRunLimitRaw = normalizeNonNegativeInteger(input.apiMonthlyRunLimit, 0);
  const maxUploadBytes = normalizeNullableInteger(input.maxUploadBytes);
  const liveEnabled = Boolean(input.liveEnabled);
  const liveRecordingEnabled = liveEnabled && Boolean(input.liveRecordingEnabled);

  return {
    apiEnabled,
    apiMonthlyRunLimit: apiEnabled ? apiMonthlyRunLimitRaw : 0,
    automationsEnabled: Boolean(input.automationsEnabled),
    currency: CURRENCY_USD,
    key,
    liveConcurrentRoomsCap: normalizePositiveIntegerWithFallback(
      input.liveConcurrentRoomsCap,
      PLAN_LIVE_DEFAULTS.liveConcurrentRoomsCap
    ),
    liveEgressMinutesMonthlyCap: liveRecordingEnabled
      ? normalizeNonNegativeInteger(
        input.liveEgressMinutesMonthlyCap,
        PLAN_LIVE_DEFAULTS.liveEgressMinutesMonthlyCap
      )
      : 0,
    liveEnabled,
    liveIdleAutoEndMinutes: normalizePositiveIntegerWithFallback(
      input.liveIdleAutoEndMinutes,
      PLAN_LIVE_DEFAULTS.liveIdleAutoEndMinutes
    ),
    liveMaxParticipantsPerRoom: normalizePositiveIntegerWithFallback(
      input.liveMaxParticipantsPerRoom,
      PLAN_LIVE_DEFAULTS.liveMaxParticipantsPerRoom
    ),
    liveMaxSessionMinutes: normalizePositiveIntegerWithFallback(
      input.liveMaxSessionMinutes,
      PLAN_LIVE_DEFAULTS.liveMaxSessionMinutes
    ),
    liveParticipantMinutesMonthlyCap: liveEnabled
      ? normalizeNonNegativeInteger(
        input.liveParticipantMinutesMonthlyCap,
        PLAN_LIVE_DEFAULTS.liveParticipantMinutesMonthlyCap
      )
      : 0,
    liveRecordingEnabled,
    maxUploadBytes,
    maxUsers: normalizeNullableInteger(input.maxUsers),
    pooledStorageBytes: normalizeNullableInteger(input.pooledStorageBytes) || DEFAULT_POOLED_STORAGE_BYTES,
    pricingModel,
    publiclyVisible: normalizePubliclyVisible(input.publiclyVisible),
    seatPrice,
    sort: normalizeNonNegativeInteger(input.sort, 0),
    stripePriceId,
    status: normalizeStatus(input.status),
    title: normalizeString(input.title) || 'Untitled Plan',
  };
}

/**
 * cleanPlanRow
 *
 * Normalizes a Firestore plan document snapshot into the UI/service shape.
 *
 * @param {object} docSnap - Firestore document snapshot.
 *
 * @returns {object} Normalized plan row.
 */
function cleanPlanRow(docSnap) {
  const row = docSnap?.data() || {};
  const normalizedRow = buildPlanPayload({
    ...row,
    key: row.key || docSnap?.id || '',
  });

  return {
    ...normalizedRow,
    id: docSnap.id,
    dateCreated: row.dateCreated || null,
    dateModified: row.dateModified || null,
  };
}

/**
 * Lists all plans from Firestore.
 *
 * @returns {Promise<Array<object>>} Plans sorted by sort, then title, then key.
 */
export async function listPlans() {
  const snapshot = await getDocs(collection(db, collections.plans));

  return snapshot.docs
    .map(cleanPlanRow)
    .sort((left, right) => {
      const sortDiff = left.sort - right.sort;
      if (sortDiff !== 0) return sortDiff;

      const titleSort = left.title.localeCompare(right.title);
      if (titleSort !== 0) return titleSort;

      return left.key.localeCompare(right.key);
    });
}

/**
 * getPlanByKey
 *
 * Reads a single plan document by key.
 *
 * @param {object} input - Read input.
 * @param {string} input.planKey - Plan key.
 *
 * @returns {Promise<object|null>} Normalized plan row or null.
 */
export async function getPlanByKey({ planKey }) {
  const cleanedPlanKey = normalizeString(planKey);
  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const planSnap = await getDoc(doc(db, collections.plans, cleanedPlanKey));
  if (!planSnap.exists()) {
    return null;
  }

  return cleanPlanRow(planSnap);
}

/**
 * Lists only active publicly visible plans for customer-facing surfaces.
 *
 * @returns {Promise<Array<object>>} Active visible plans sorted by sort, then title, then key.
 */
export async function listActivePlans() {
  const rows = await listPlans();
  return rows.filter((row) => row.status === PLAN_STATUS.ACTIVE && row.publiclyVisible === true);
}

/**
 * Creates a new plan document in Firestore.
 *
 * @param {object} input - Create input.
 *
 * @returns {Promise<object>} Created plan summary.
 */
export async function createPlan(input = {}) {
  const payload = buildPlanPayload(input);
  const planKey = payload.key;

  await setDoc(
    doc(db, collections.plans, planKey),
    {
      ...payload,
      dateCreated: serverTimestamp(),
      dateModified: serverTimestamp(),
    },
    { merge: false }
  );

  return payload;
}

/**
 * Updates a plan document with full normalized field values.
 *
 * @param {object} input - Update input.
 * @param {string} input.planKey - Existing immutable plan key.
 * @param {object} input.plan - Full plan payload to persist.
 *
 * @returns {Promise<void>}
 */
export async function updatePlan({ planKey, plan }) {
  const cleanedPlanKey = normalizeString(planKey);

  if (!cleanedPlanKey) {
    throw new Error('planKey is required');
  }

  const payload = buildPlanPayload({
    ...plan,
    key: cleanedPlanKey,
  });

  await updateDoc(doc(db, collections.plans, cleanedPlanKey), {
    ...payload,
    dateModified: serverTimestamp(),
  });
}

/**
 * Reorders plans and persists zero-based sort values in batch.
 *
 * @param {Array<string>} orderedPlanKeys - Plan keys in final order.
 *
 * @returns {Promise<void>}
 */
export async function reorderPlans(orderedPlanKeys = []) {
  if (!Array.isArray(orderedPlanKeys) || !orderedPlanKeys.length) {
    return;
  }

  const batch = writeBatch(db);

  orderedPlanKeys.forEach((rawKey, index) => {
    const planKey = normalizeString(rawKey);
    if (!planKey) {
      return;
    }

    batch.update(doc(db, collections.plans, planKey), {
      dateModified: serverTimestamp(),
      sort: index,
    });
  });

  await batch.commit();
}
